/**
 * Missing-data dialog for the Dynamic Template Provider (MD-148, Phase 3).
 *
 * Opened when a template is dropped on the calendar but its address and/or
 * per-therapy visit fee could not be inferred from the Rx's existing
 * appointments (typically a brand-new Rx with no prior appointments).
 *
 * Address is a hard requirement and the visit fee must be a deliberate choice
 * per therapy. The dialog pre-fills both with the same logic NewRx uses
 * (address from rx.isHomeVisit + patient address flags; fee code HB/HB2 from
 * the chosen address type, mapped to a fee id via the master tables) and lets
 * the user confirm or override before the atomic create-and-schedule runs.
 */

import React, { useMemo, useState } from "react";
import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DialogDefaultAction from "../../../atoms/DialogDefaultAction";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
import { useRxLackingAppointments } from "../../../stores/Rx/rx.queries";
import { useMasterTablesFacade } from "../../../stores/MasterTables/masterTables.utils";
import { useCreateAndScheduleAppointment } from "../../../stores/Appointment/appointment.mutation";
import { slotTherapy } from "../../../helpers/slotTherapy";
import { AddressDto } from "../../../api-client";
import { feeCodeForAddress, pickDefaultAddress } from "./templateAddress";
import { useTemplateAddresses } from "./useTemplateAddresses";

/** Explicit "no fee" sentinel for the per-therapy select (maps to null). */
const NO_FEE = "__none__";

/** Human label for an address option. */
const addressLabel = (a: AddressDto): string => {
  const c = a.contactInfo;
  const lines = [c?.addressLine1, [c?.zip, c?.city].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  const flags = [
    a.isVisit ? "Besuch" : null,
    a.isPrimary ? "Primär" : null,
  ].filter(Boolean);
  const prefix = a.type ? `${a.type}` : "Adresse";
  return [prefix, lines, flags.length ? `(${flags.join(", ")})` : null]
    .filter(Boolean)
    .join(" · ");
};

const TemplateScheduleDialog: React.FC = observer(() => {
  const CalendarStore = useStore(CalendarContext);
  const { templateScheduleDialogOpen, templateScheduleDialogData, closeTemplateScheduleDialog } =
    CalendarStore;

  const payload = templateScheduleDialogData;
  const patientId = payload?.patientId;

  const { data: rxLacking = [] } = useRxLackingAppointments(patientId ?? 0);
  const { getTherapyId } = useMasterTablesFacade();
  const { mutateAsync: createAndSchedule, isPending } = useCreateAndScheduleAppointment();

  // Patient addresses PLUS the current location's practice address. The
  // practice must always be offerable: it is the only option for a practice Rx,
  // and for a home-visit Rx it is the alternative the user has to be able to
  // pick. It is not a patient address, so without this merge the picker could
  // never show it.
  const { addresses, practiceAddress } = useTemplateAddresses(patientId);

  // The Rx this template belongs to, and the therapy metadata we need to map
  // fee codes to fee ids (tarifGroup) and to label each row (abbreviation).
  const rx = useMemo(
    () => rxLacking.find((r: any) => String(r?.id) === payload?.rxId),
    [rxLacking, payload?.rxId]
  );
  // A template is unscheduled, so its therapyRx has no line item: the label
  // comes from the PRESCRIBED term (slotTherapy), and the tarif group from the
  // Rx — `TherapyRxDto.therapy`, which used to supply both, no longer exists.
  // Reading it left every fee row labelled "Hausbesuchspauschale " with a blank
  // therapy and, worse, an empty tarifGroupId, so getTherapyId could never
  // resolve the automatic HB/HB2 fee.
  const tarifGroupId = String((rx as any)?.tarifGroupId ?? (rx as any)?.tarifGroup?.id ?? "");
  const therapies = useMemo(() => {
    const therapyRxs: any[] = Array.isArray(rx?.therapyRxs) ? rx!.therapyRxs : [];
    return (payload?.therapyRxIds ?? []).map((id) => {
      const trx = therapyRxs.find((t) => String(t?.id) === id);
      return {
        therapyRxId: id,
        abbreviation: slotTherapy({ therapyRx: trx })?.abbreviation ?? "",
        tarifGroupId,
      };
    });
  }, [rx, payload?.therapyRxIds, tarifGroupId]);

  // Default address: inferred id if still present, else NewRx-style smart pick.
  // Empty until the patient/location addresses have loaded.
  const defaultAddressId = useMemo(() => {
    if (payload?.inferredAddressId) return payload.inferredAddressId;
    return pickDefaultAddress(addresses, rx?.isHomeVisit)?.id ?? "";
  }, [payload?.inferredAddressId, addresses, rx?.isHomeVisit]);

  const [addressId, setAddressId] = useState<string>("");
  // Per-therapy fee selection keyed by therapyRxId. Value is a fee id, NO_FEE,
  // or "" (auto = follow the address's derived fee).
  const [feeChoice, setFeeChoice] = useState<Record<string, string>>({});
  // Whether the user has taken over the address. Until they have, a late-
  // arriving default may still claim the field — see below.
  const [addressTouched, setAddressTouched] = useState(false);

  // Re-seed local state whenever a new payload opens the dialog.
  const seedKey = `${payload?.rxId ?? ""}:${payload?.start ?? ""}`;
  const [seededFor, setSeededFor] = useState<string>("");
  if (payload && seededFor !== seedKey) {
    setSeededFor(seedKey);
    setAddressTouched(false);
    setAddressId(defaultAddressId);
    const seededFees: Record<string, string> = {};
    for (const t of therapies) {
      const inferred = payload.inferredVisitFees?.[t.therapyRxId];
      if (inferred === null) seededFees[t.therapyRxId] = NO_FEE;
      else if (typeof inferred === "string") seededFees[t.therapyRxId] = inferred;
      else seededFees[t.therapyRxId] = ""; // auto-follow address
    }
    setFeeChoice(seededFees);
  }

  // The dialog opens on a drop, so the patient / location / Rx queries are
  // usually still in flight at seed time and `defaultAddressId` is "" then.
  // Seeding is keyed to the payload, so without this the picker would keep that
  // empty value for the whole dialog even once the addresses arrive — the user
  // sees no preselected address, and the address is mandatory. Adopt the
  // default as soon as it resolves, but never overwrite a real choice.
  if (payload && !addressTouched && !addressId && defaultAddressId) {
    setAddressId(defaultAddressId);
  }

  const selectedAddress = addresses.find((a) => a.id === addressId);
  const derivedFeeCode = feeCodeForAddress(selectedAddress);
  // Praxis address (no fee code) forbids visit fees entirely.
  const feesDisabled = derivedFeeCode === null;

  const selectAddress = (id: string) => {
    setAddressTouched(true);
    setAddressId(id);
  };

  // Practice-visit option: for a home-visit Rx the user can still choose to
  // schedule this appointment at the practice (no visit fee), mirroring the
  // NewRx template flow on the Patient page. `practiceAddress` comes from the
  // current location via useTemplateAddresses — the same one the primed handles
  // use — so this option is offered whenever the location has an address.
  const showPracticeOption = rx?.isHomeVisit === true && !!practiceAddress;
  const isPracticeVisit = !!practiceAddress && addressId === practiceAddress.id;

  const handlePracticeVisitToggle = (checked: boolean) => {
    if (checked) {
      if (practiceAddress?.id) selectAddress(practiceAddress.id);
      return;
    }
    // Revert to the home-visit address. If the patient has none on file there
    // is nothing to revert TO — an appointment cannot be addressless, so the
    // practice stays selected and the user can still pick from the list.
    const homeVisit = pickDefaultAddress(addresses, true);
    if (homeVisit?.id && homeVisit.id !== practiceAddress?.id) {
      selectAddress(homeVisit.id);
    }
  };

  /** Resolve the final fee id (or null) for a therapy given the current choice. */
  const resolveFee = (therapyRxId: string, tarifGroupId: string): string | null => {
    if (feesDisabled) return null;
    const choice = feeChoice[therapyRxId];
    if (choice === NO_FEE) return null;
    if (choice && choice !== "") return choice; // explicit fee id
    // Auto: derive from the address's fee code.
    if (!derivedFeeCode) return null;
    return getTherapyId(tarifGroupId, derivedFeeCode);
  };

  const handleClose = () => {
    if (isPending) return;
    closeTemplateScheduleDialog();
  };

  const handleConfirm = async () => {
    if (!payload || !addressId) return;
    const visitFees: Record<string, string | null> = {};
    for (const t of therapies) {
      visitFees[t.therapyRxId] = resolveFee(t.therapyRxId, t.tarifGroupId);
    }
    await createAndSchedule({
      dto: {
        therapyRxIds: payload.therapyRxIds,
        start: payload.start,
        userId: payload.userId,
        addressId,
        visitFees,
      },
      patientId: payload.patientId,
    });
    closeTemplateScheduleDialog();
  };

  if (!templateScheduleDialogOpen || !payload) return null;

  return (
    <Dialog open={templateScheduleDialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Termin planen – Angaben erforderlich</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Für dieses Rezept sind Adresse und Hausbesuchspauschale noch nicht hinterlegt.
            Bitte bestätigen oder anpassen.
          </Typography>

          {showPracticeOption && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPracticeVisit}
                  onChange={(e) => handlePracticeVisitToggle(e.target.checked)}
                />
              }
              label="Als Praxistermin planen (keine Hausbesuchspauschale)"
            />
          )}

          <FormControl fullWidth size="small">
            <InputLabel id="template-address-label">Adresse</InputLabel>
            <Select
              labelId="template-address-label"
              label="Adresse"
              value={addressId}
              onChange={(e) => selectAddress(e.target.value)}
            >
              {addresses.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {addressLabel(a)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {therapies.map((t) => {
            const autoFeeId = derivedFeeCode
              ? getTherapyId(t.tarifGroupId, derivedFeeCode)
              : null;
            const autoLabel = feesDisabled
              ? "Keine (Praxis)"
              : derivedFeeCode
                ? `Automatisch (${derivedFeeCode})`
                : "Keine";
            return (
              <FormControl key={t.therapyRxId} fullWidth size="small" disabled={feesDisabled}>
                <InputLabel id={`fee-${t.therapyRxId}-label`}>
                  Hausbesuchspauschale {t.abbreviation}
                </InputLabel>
                <Select
                  labelId={`fee-${t.therapyRxId}-label`}
                  label={`Hausbesuchspauschale ${t.abbreviation}`}
                  value={feesDisabled ? NO_FEE : feeChoice[t.therapyRxId] ?? ""}
                  onChange={(e) =>
                    setFeeChoice((prev) => ({ ...prev, [t.therapyRxId]: e.target.value }))
                  }
                >
                  <MenuItem value="">{autoLabel}</MenuItem>
                  {!feesDisabled && autoFeeId && derivedFeeCode && (
                    <MenuItem value={autoFeeId}>{derivedFeeCode}</MenuItem>
                  )}
                  <MenuItem value={NO_FEE}>Keine</MenuItem>
                </Select>
              </FormControl>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isPending || !addressId}
          autoFocus
        >
          Planen
        </Button>
      </DialogActions>
      <DialogDefaultAction onAction={handleConfirm} disabled={isPending || !addressId} />
    </Dialog>
  );
});

TemplateScheduleDialog.displayName = "TemplateScheduleDialog";

export default TemplateScheduleDialog;
